import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Mail, Phone, DollarSign, CheckCircle, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DonationsManager() {
  const [donations, setDonations] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('requests');

  const fetchData = useCallback(async () => {
    try {
      const [donsRes, paysRes] = await Promise.all([
        axios.get(`${API}/admin/donations`, { withCredentials: true }),
        axios.get(`${API}/admin/payments`, { withCredentials: true })
      ]);
      setDonations(donsRes.data);
      setPayments(paysRes.data);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const totalPaid = payments.filter(p => p.payment_status === 'paid').reduce((sum, p) => sum + p.amount, 0);

  return (
    <div data-testid="donations-manager">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-[#2E332D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Gestion des Dons
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="card">
          <div className="flex items-center gap-3">
            <DollarSign className="text-[#4A6B48]" size={32} />
            <div>
              <p className="text-2xl font-bold text-[#2E332D]">${totalPaid.toFixed(2)}</p>
              <p className="text-sm text-[#5C665A]">Total reçu</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center gap-3">
            <CheckCircle className="text-[#4A6B48]" size={32} />
            <div>
              <p className="text-2xl font-bold text-[#2E332D]">{payments.filter(p => p.payment_status === 'paid').length}</p>
              <p className="text-sm text-[#5C665A]">Paiements complétés</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center gap-3">
            <Clock className="text-[#D87D4A]" size={32} />
            <div>
              <p className="text-2xl font-bold text-[#2E332D]">{donations.length}</p>
              <p className="text-sm text-[#5C665A]">Demandes</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-2 mb-6">
        <button onClick={() => setTab('requests')} className={`px-4 py-2 rounded-full text-sm ${tab === 'requests' ? 'bg-[#4A6B48] text-white' : 'bg-white border border-[#E5E3DB]'}`}>
          Demandes simples ({donations.length})
        </button>
        <button onClick={() => setTab('payments')} className={`px-4 py-2 rounded-full text-sm ${tab === 'payments' ? 'bg-[#4A6B48] text-white' : 'bg-white border border-[#E5E3DB]'}`}>
          Paiements Stripe ({payments.length})
        </button>
      </div>

      {loading && (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4A6B48]"></div></div>
      )}

      {!loading && tab === 'requests' && (
        <div className="space-y-4">
          {donations.length === 0 && (
            <p className="text-center text-[#5C665A] py-8">Aucune demande de don reçue.</p>
          )}
          {donations.map((d) => (
            <div key={d.donation_id} className="card">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-[#2E332D]">{d.name}</h3>
                  <div className="mt-2 space-y-1 text-sm text-[#5C665A]">
                    <div className="flex items-center gap-2"><Mail size={14} />{d.email}</div>
                    {d.phone && <div className="flex items-center gap-2"><Phone size={14} />{d.phone}</div>}
                    {d.amount && <div className="flex items-center gap-2 font-semibold text-[#4A6B48]"><DollarSign size={14} />${d.amount}</div>}
                  </div>
                  <p className="text-sm text-[#5C665A] mt-3 italic">&ldquo;{d.message}&rdquo;</p>
                  <p className="text-xs text-[#5C665A] mt-2">{new Date(d.created_at).toLocaleDateString('fr-FR')}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && tab === 'payments' && (
        <div className="space-y-4">
          {payments.length === 0 && (
            <p className="text-center text-[#5C665A] py-8">Aucun paiement effectué.</p>
          )}
          {payments.map((p) => {
            const isPaid = p.payment_status === 'paid';
            const statusClass = isPaid ? 'text-[#4A6B48] bg-[#4A6B48]/10' : 'text-orange-600 bg-orange-100';
            const statusLabel = isPaid ? 'Payé' : 'En attente';
            return (
              <div key={p.payment_id} className="card">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`text-xs font-medium px-2 py-1 rounded ${statusClass}`}>
                        {statusLabel}
                      </span>
                      <span className="text-lg font-bold text-[#4A6B48]">${p.amount.toFixed(2)} {p.currency.toUpperCase()}</span>
                    </div>
                    <h3 className="text-lg font-bold text-[#2E332D]">{p.donor_name}</h3>
                    <p className="text-sm text-[#5C665A]">{p.donor_email}</p>
                    <p className="text-xs text-[#5C665A] mt-2">{new Date(p.created_at).toLocaleDateString('fr-FR')}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
